import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Printer } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { API_ENDPOINTS } from '@/utils/constants';
import { DailyReportData } from '@/services/types';
import { formatCurrency } from '@/utils/formatters';
import { Alert, Button, Card, IconButton, LoadingSkeleton, Overline } from '@/components/ui';
import { CashierCard, HourlyCard } from './ReportCards';
import { DateRangePicker, DateRange } from '@/components/ui/DateRangePicker';
import { PageShell } from '@/components/layout/PageShell';
import { rangeFor, toLocalDateTimeParams } from '@/utils/dateRange';

/** 16b Laporan harian. Phone: T3 stack. Landscape: plum financial card | top products table + per-cashier tiles + hourly bars. */
const DailyReportPage: React.FC = () => {
  const { t } = useTranslation();
  const { selectedShop } = useAppSelector((state) => state.auth);
  const [data, setData] = useState<DailyReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(() => rangeFor('today'));

  const fetchReport = useCallback(async () => {
    if (!selectedShop) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(API_ENDPOINTS.DAILY_REPORT(selectedShop.shopId), { params: toLocalDateTimeParams(dateRange) });
      setData(response.data.data);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedShop, dateRange]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  if (!selectedShop) return null;

  const avg = data && data.transactionCount > 0 ? data.netSales / data.transactionCount : 0;
  const mix = data ? [
    { label: t('dashboard.cash'), value: data.cashSales, color: 'bg-primary-500' },
    { label: 'QRIS', value: data.qrisSales, color: 'bg-accent-500' },
    { label: 'rWallet', value: data.ewalletSales, color: 'bg-primary-200' },
    { label: t('dashboard.transfer'), value: data.transferSales, color: 'bg-success-500' },
    { label: t('dashboard.card'), value: data.cardSales, color: 'bg-info-500' },
  ].filter((m) => m.value > 0) : [];
  const mixTotal = mix.reduce((n, m) => n + m.value, 0);

  const financial = data && (
    <div className="flex flex-col gap-3.5 rounded-xl bg-surface-brand p-[18px] text-white shadow-brand lg:h-full lg:p-6">
      <Overline tone="onBrand">{t('report.financialSummary')}</Overline>
      <div className="lg:hidden">
        <span className="text-[34px] font-black leading-[1.1] tnum tracking-[-0.02em]">{formatCurrency(data.netSales)}</span>
        <p className="text-[13px] text-primary-100">{t('report.transactionsAvg', { count: data.transactionCount, amount: formatCurrency(avg) })}</p>
      </div>
      <div className="grid grid-cols-3 gap-2 border-t border-white/[.16] pt-3 tnum lg:flex lg:flex-col lg:gap-3 lg:border-0 lg:pt-0">
        {[[t('report.grossSales'), formatCurrency(data.grossSales)], [t('report.refunds'), `−${formatCurrency(data.totalRefunds)}`], [t('report.voids'), String(data.totalVoids)], [t('report.tax'), `+${formatCurrency(data.totalTax || 0)}`], [t('report.serviceCharge'), `+${formatCurrency(data.totalServiceCharge || 0)}`]].map(([l, v]) => (
          <div key={l} className="flex flex-col lg:flex-row lg:justify-between lg:text-[15px]"><span className="text-[11px] text-primary-100 lg:text-[15px]">{l}</span><span className="text-[13px] font-extrabold lg:text-[15px]">{v}</span></div>
        ))}
      </div>
      <div className="hidden flex-1 lg:block" />
      <div className="hidden flex-col gap-1 border-t border-white/[.16] pt-4 lg:flex">
        <span className="text-sm font-bold text-primary-100">{t('dashboard.netSales')}</span>
        <span className="text-[44px] font-black leading-[1.1] tnum">{formatCurrency(data.netSales)}</span>
        <span className="text-[13px] text-primary-100">{t('report.transactionsAvg', { count: data.transactionCount, amount: formatCurrency(avg) })}</span>
      </div>
    </div>
  );

  const mixCard = data && (
    <Card padding="sm" className="flex flex-col gap-2.5">
      <span className="text-[15px] font-extrabold">{t('dashboard.paymentBreakdown')}</span>
      {mixTotal > 0 ? (
        <>
          <div className="flex h-2.5 gap-[3px] overflow-hidden rounded-pill">{mix.map((m) => <span key={m.label} className={m.color} style={{ width: `${(m.value / mixTotal) * 100}%` }} />)}</div>
          <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-xs font-bold text-ink-secondary">{mix.map((m) => <span key={m.label} className="flex items-center gap-1.5"><span className={clsx('h-2 w-2 rounded-full', m.color)} />{m.label} {Math.round((m.value / mixTotal) * 100)}%</span>)}</div>
        </>
      ) : <p className="text-sm text-ink-muted">{t('dashboard.noData')}</p>}
    </Card>
  );

  const topCard = data && (
    <Card padding="sm" className="lg:p-[18px_22px]">
      <div className="hidden grid-cols-[36px_minmax(0,1fr)_90px_130px] gap-3 border-b border-line-subtle pb-2 text-xs font-extrabold text-ink-muted lg:grid">
        <span>#</span><span>{t('dashboard.topProducts')}</span><span className="text-right">{t('report.sold')}</span><span className="text-right">{t('report.sales')}</span>
      </div>
      <span className="text-[15px] font-extrabold lg:hidden">{t('dashboard.topProducts')}</span>
      {data.topProducts.length === 0 ? <p className="pt-2 text-sm text-ink-muted">{t('dashboard.noData')}</p> : data.topProducts.slice(0, 5).map((p, i) => (
        <div key={p.compositeId} className="flex items-center gap-2.5 border-b border-line-subtle py-2.5 text-sm tnum last:border-b-0 lg:grid lg:grid-cols-[36px_minmax(0,1fr)_90px_130px] lg:gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-[11px] font-black">{i + 1}</span>
          <span className="flex min-w-0 flex-1 flex-col lg:block"><span className="truncate font-bold">{p.name}</span><span className="text-xs text-ink-muted lg:hidden">{p.quantitySold} {t('dashboard.sold')}</span></span>
          <span className="hidden text-right text-ink-secondary lg:block">{p.quantitySold}</span>
          <span className="text-right font-extrabold">{formatCurrency(p.totalSales)}</span>
        </div>
      ))}
    </Card>
  );

  const cashierCard = data && <CashierCard data={data} />;

  const hourlyCard = data && <HourlyCard data={data} />;

  const printButton = <IconButton variant="card" label={t('report.printReport')} onClick={() => window.print()} className="!text-ink-brand lg:hidden"><Printer /></IconButton>;

  return (
    <PageShell
      title={t('report.title')}
      backTo="/settings"
      actions={<>
        <DateRangePicker value={dateRange} onChange={setDateRange} className="hidden lg:flex" />
        <Button variant="secondary" size="md" icon={<Printer />} onClick={() => window.print()} className="hidden lg:inline-flex">{t('report.printReport')}</Button>
        {printButton}
      </>}
    >
      <div className="flex flex-col gap-3 pb-6 lg:gap-4">
        <DateRangePicker value={dateRange} onChange={setDateRange} className="self-start lg:hidden" />
        {error && <Alert variant="error" message={error} />}
        {isLoading && !data ? <><LoadingSkeleton height={160} /><LoadingSkeleton height={120} /></> : data && (
          <>
            <div className={clsx('flex flex-col gap-3 lg:hidden', isLoading && 'opacity-60')}>{financial}{mixCard}{topCard}{cashierCard}{hourlyCard}</div>
            <div className={clsx('hidden gap-4 lg:grid lg:grid-cols-[380px_minmax(0,1fr)]', isLoading && 'opacity-60')}>
              {financial}
              <div className="flex flex-col gap-4">{topCard}{cashierCard}{hourlyCard}{mixCard}</div>
            </div>
          </>
        )}
      </div>
    </PageShell>
  );
};

export default DailyReportPage;
